import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import { trackEvent } from "utility/mixpanel";
import { logError } from "utility/Function";
import {
  setOriStarterMessage,
  wasStarterSentRecently,
} from "apps/user/learner/lib/ori-starter-message";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useAuth } from "providers/auth";
import { useTranslation } from "lib/i18n/use-translation";
import appInfo from "utility/app-info";
import { LEARNER_ASSESSMENT_REPORT_SCORES } from "utility/graphQl/query";
import { RATE_ASSESSMENT_REPORT } from "utility/graphQl/mutation";
import ReportShell, {
  ReportShellDeparting,
  ReportStepScore,
} from "components/report/ReportShell";
import {
  measureScoreParts,
  ScorePartRects,
} from "components/report/score-carry";
import { PageError } from "apps/user/learner/components/page-container";
import { Button } from "components/ui/Button";
import OriMark from "Assets/icon/ori-mark.svg";
import { ReactComponent as MindIcon } from "Assets/icon/domain/mind.svg";
import { ReactComponent as MovementIcon } from "Assets/icon/domain/movement.svg";
import { ReactComponent as NutritionIcon } from "Assets/icon/domain/nutrition.svg";
import { ReactComponent as PersonalCapacityIcon } from "Assets/icon/domain/personal-capacity.svg";
import { ReactComponent as SleepIcon } from "Assets/icon/domain/sleep.svg";
import { ReactComponent as SocialIcon } from "Assets/icon/domain/social.svg";
import { ReactComponent as WorkCapacityIcon } from "Assets/icon/domain/work-capacity.svg";
import styles from "./AssessmentReport.module.scss";
import useStepSnapshot from "apps/user/learner/hooks/use-step-snapshot";
import ReportReadyStep from "./ReportReadyStep";
import { formatCheckInDate } from "./checkInDate";
import ScoreRevealStep from "./steps/ScoreRevealStep";
import SummaryNarrativeStep from "./steps/SummaryNarrativeStep";
import ScoreBreakdownStep from "./steps/ScoreBreakdownStep";
import DomainHighlightsStep from "./steps/DomainHighlightsStep";
import ChatWithOriStep from "./steps/ChatWithOriStep";
import CheckInToast from "./CheckInToast";
import ScoreRangeStep, { ScoreBandTone } from "./steps/ScoreRangeStep";
import SharePreview, {
  ImageShareMethod,
  ShareCardProps,
} from "./share/SharePreview";
import { useReportJourney, useReportSource } from "./use-report-journey";
import WoneLogo from "Assets/wone_logo.svg";
import SkyPosterTall from "Assets/images/report-sky-poster-tall.webp";
import { ReportLocationState } from "./reopen";

export const INDEX_TAB_PATH = "/user/woneindex";
const ORI_PATH = "/user/chat";

/** Names this surface in Ori's entry-point analytics. */
const ORI_ENTRY_POINT = "assessment_report";

const HIGHLIGHT_COUNT = 3;

/**
 * A goal on each of the lowest domains, and nothing else. The same three the
 * growth step shows, in the same order, so the screen the learner commits on
 * offers exactly what the screen before it named.
 */
const GOAL_CHOICE_COUNT = HIGHLIGHT_COUNT;

/** Calendar arithmetic for the benchmark's footnote, which speaks in months. */
const DAYS_PER_MONTH = 30;
const MONTHS_PER_YEAR = 12;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** How long a screen takes to fade out before the next fades in. */
const FADE_MS = 300;

/** How long the share button says it copied before offering to again. */
const COPIED_CONFIRMATION_MS = 2500;

/**
 * How often the report is asked for again while its copy is still being
 * written. Generation runs in the background after submit and takes around
 * fifteen seconds, so a learner arriving before it finishes is the ordinary
 * case rather than the edge one. Once a second, so the summary shows within
 * about a second of Coach writing it.
 */
const REPORT_POLL_INTERVAL_MS = 1000;

/**
 * How long after submit to keep asking before settling for the fallback copy.
 * Generation ordinarily takes about fifteen seconds, so this is three times
 * the usual wait. Coach allows a failing call up to 180 seconds with retries,
 * but copy that arrives that late is rare, and a learner looking at
 * placeholder bars for minutes is worse off than one reading the stand-in.
 */
const REPORT_COPY_WAIT_MS = 45000;

/**
 * Whether the ancient wisdom card shows the catalogue copy when nothing was
 * generated for it. The content owner marked that string "use only if the
 * field must be shown" and has not settled the question, so the card is left
 * out rather than filled with an approximation of the most personal of the
 * five parts. Awaiting their decision; flipping this is the whole change.
 */
const SHOW_ANCIENT_WISDOM_FALLBACK = false;

/**
 * Whole months the benchmark's window spans, for the footnote. Rounded,
 * because the copy speaks in months and a window is not going to land on an
 * exact boundary. `null` when either bound fails to parse, so the caller
 * leaves the sentence out rather than printing NaN.
 */
const monthsBetween = (start: string, end: string): number | null => {
  const from = new Date(start);
  const to = new Date(end);
  const days = (to.getTime() - from.getTime()) / MS_PER_DAY;
  if (!Number.isFinite(days)) return null;
  return Math.max(1, Math.round(days / DAYS_PER_MONTH));
};

const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

interface ReportStep {
  key: string;
  node: React.ReactNode;
  /**
   * Plays on a timer and moves the report on by itself, so stepping back
   * onto it would carry the learner straight forward again. `back` skips it.
   */
  transient?: boolean;
  /**
   * The back and forward controls this step offers. Drawn by the shell
   * rather than the step, so they hold still while the content fades.
   * Omitted on steps that offer none, or own their bottom row as the last
   * step does.
   */
  nav?: ReportStepNav;
  /**
   * The score readout in the shell's foot corner. Omitted where the step
   * shows the score itself, as the summary does, or puts something else
   * there, as the last step does.
   */
  readout?: ReportStepScore;
  /**
   * Leaves the close off. The designs draw none on the cover, which starts
   * the report, on the reveal, which moves on by itself, or on the last
   * step, which ends it with its own Done.
   */
  withoutClose?: boolean;
  /**
   * Shows the sky at full brightness, without the contrast tint. The designs
   * light the cover and the reveal this way; the tint arrives with the first
   * screen of content to read.
   */
  brightSky?: boolean;
}

interface ReportStepNav {
  onBack: () => void;
  backLabel: string;
  onForward: () => void;
  forwardLabel: string;
}

/** The population comparison, as the response shapes it. */
interface PopulationBenchmarkNode {
  percentile: number;
  averageValue: number;
  sampleSize: number;
  windowStart: string;
  windowEnd: string;
  distribution: { score: number; density: number }[];
}

/**
 * The report's written copy, as the response shapes it.
 *
 * Absent entirely where the response predates the field or the learner's
 * company is not generating reports, which reads the same as UNAVAILABLE:
 * the catalogue copy stands in and nothing is asked for again.
 */
interface ReportCopyNode {
  status: "PENDING" | "READY" | "UNAVAILABLE";
  /**
   * The summary is written and the rest is still on its way. The status stays
   * PENDING; the headline, modern measurement and ancient wisdom carry their
   * copy and the other parts stay empty until READY.
   */
  partialAvailable: boolean;
  reportHeadline: string;
  modernMeasurement: string;
  ancientWisdom: string;
  strengthsFocus: string;
  areasForGrowth: string;
  /** The domain the copy was written about. */
  anchorDomain: string;
  /**
   * The growth domain the copy bridges to. Null is an answer rather than a
   * gap: there was no genuine bridge, and the copy names none.
   */
  leadGrowthDomain: string | null;
}

/** One band of `scoreScale`, as the response shapes it. */
interface ScoreBandNode {
  key: string;
  name: string;
  minValue: number;
  maxValue: number;
}

interface ScoreNode {
  id: string;
  /** The schema's stable identifier. `id` is a database key and `name` is translated. */
  shorthand?: string | null;
  /** On a theme, the domain it rolls up to. Required in the schema. */
  domainShorthand?: string | null;
  name?: string | null;
  value?: number | null;
  minValue?: number | null;
  maxValue?: number | null;
  label?: string | null;
  /** The band's stable identifier, which the display text is not. */
  labelKey?: string | null;
  blurb?: string | null;
}

/**
 * The designs label each bar with its domain's glyph. The map lives here
 * rather than in the step because the step takes artwork as a prop and
 * bundles none of its own, and it is keyed on `shorthand` because `id` is a
 * database key and `name` is translated.
 *
 * HRV Readiness has a glyph in the designs and no entry here: the web
 * query asks for `indexScores(excludeBiometrics: true)`, so that domain
 * never reaches this screen.
 */
const DOMAIN_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  domain_mind: MindIcon,
  domain_movement: MovementIcon,
  domain_nutrition: NutritionIcon,
  domain_personal_stress: PersonalCapacityIcon,
  domain_sleep: SleepIcon,
  domain_social: SocialIcon,
  domain_work_stress: WorkCapacityIcon,
};

/**
 * The order the breakdown screen draws the domains in: clockwise round the
 * radar from the top, and left to right along the bars. One list for both,
 * so the two charts on the screen cannot disagree. The server sorts domains
 * by display order held in data, which can be edited, so the screen keeps
 * the designed order itself. Its eighth position in the designs is HRV
 * Readiness, absent here for the same reason its glyph is.
 */
const BREAKDOWN_ORDER = [
  "domain_mind",
  "domain_social",
  "domain_sleep",
  "domain_movement",
  "domain_nutrition",
  "domain_work_stress",
  "domain_personal_stress",
];

/**
 * How the frame colours each boundary, keyed on the band's stable key.
 *
 * This is all that stays here. The bands themselves, their names and their
 * bounds come from `scoreScale`, because the thresholds live in one module
 * server-side and a second copy misplaces learners on a scale that still
 * reads as authoritative. Colour is not a threshold: it is the frame's own
 * ramp, read off its boundary dots, and no server sends it.
 *
 * A band with no entry renders uncoloured rather than breaking the screen,
 * so the server can add one before we have a colour for it.
 */
const BAND_TONES: Record<string, ScoreBandTone> = {
  needsRestoration: "critical",
  needsNurturing: "low",
  worthWatching: "moderate",
  // Where the scale crosses into "fine", which the frame gives a neutral of
  // its own between the amber below and the green above.
  steady: "neutral",
  strong: "good",
  thriving: "good",
};

// How wide the web frame draws each band's column along the range step's
// axis, in its pixels. Not to scale: the lower four bands take about the same
// width whatever their score ranges, so Strong's ten points are not squeezed
// under its name. Placement within a column still follows the bounds the
// server sends.
const BAND_WIDTHS: Record<string, number> = {
  needsRestoration: 174,
  needsNurturing: 176,
  worthWatching: 176,
  steady: 177,
  strong: 85,
  thriving: 120,
};

// A domain not listed sorts after the ones that are, in the order it came.
const breakdownPosition = (domain: ScoreNode) => {
  const at = domain.shorthand ? BREAKDOWN_ORDER.indexOf(domain.shorthand) : -1;
  return at === -1 ? BREAKDOWN_ORDER.length : at;
};

const byBreakdownOrder = (a: ScoreNode, b: ScoreNode) =>
  breakdownPosition(a) - breakdownPosition(b);

/**
 * The tile for the domain the generated copy names.
 *
 * The copy carries a shorthand and the tiles are keyed on the domain's
 * database id, so the two are matched here. Undefined where the copy names
 * nothing, or names a domain this group does not show, and the step then
 * stays on its first tile.
 */
const tileIdFor = (
  shorthand: string | null | undefined,
  group: ScoreNode[]
): string | undefined =>
  shorthand
    ? group.find((domain) => domain.shorthand === shorthand)?.id
    : undefined;

const iconFor = (domain: ScoreNode) => {
  const Icon = domain.shorthand ? DOMAIN_ICONS[domain.shorthand] : undefined;
  return Icon ? <Icon aria-hidden="true" /> : undefined;
};

// Either mechanism will do. Without both there is nowhere for the sentence
// to go, and the button stays as it was drawn: present, and not pressable.
const canShareText = (): boolean =>
  typeof navigator !== "undefined" &&
  (typeof navigator.share === "function" ||
    typeof navigator.clipboard?.writeText === "function");

const isScored = (score: ScoreNode): boolean =>
  typeof score.value === "number" && Number.isFinite(score.value);

const byValueDescending = (a: ScoreNode, b: ScoreNode) =>
  (b.value ?? 0) - (a.value ?? 0);

const AssessmentReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation(["assessment", "common"]);
  // Set while the share image's preview is up.
  const [previewingShare, setPreviewingShare] = useState(false);
  const [step, setStep] = useState(0);
  // Set until a reopened report has moved past its cover and reveal. Held in
  // state rather than read off the location each render, so stepping back to
  // the cover or replaying from the end still works as it does first time.
  const [openingOnContent, setOpeningOnContent] = useState(
    (location.state as ReportLocationState | null)?.reopened === true
  );
  // The rating this learner has given the report, so the thumbs can show it
  // back. Local to the visit: nothing reads a rating back out, so a replay
  // starts the question again rather than claiming an answer it cannot prove.
  const [feedbackHelpful, setFeedbackHelpful] = useState<boolean | null>(null);
  // The check-in toast is the learner's once per report. It is claimed the
  // first time they are on the last step with an upcoming booking, which may
  // be after arriving if the date lands late, and the date it shows is kept
  // from then. Arriving with no booking waits for one, but leaving the step
  // while waiting spends the toast. Coming back to the step afterwards does
  // not repeat it, and a reopened report never shows it.
  const [toast, setToast] = useState<{
    phase: "unseen" | "waiting" | "showing" | "spent";
    date: string | null;
  }>({ phase: "unseen", date: null });
  const reopened =
    (location.state as ReportLocationState | null)?.reopened === true;
  // Set while the copied confirmation is on the button, where there is no
  // share sheet to confirm on the learner's behalf.
  const [shareCopied, setShareCopied] = useState(false);
  const copiedTimer = useRef<number>();
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);
  const [fading, setFading] = useState(false);
  // What of the foot row the step being faded to does not have, so those
  // parts go out with the stage rather than vanishing once it has gone.
  const [departing, setDeparting] = useState<ReportShellDeparting>();

  // Held in a ref as well as state: the guard has to read the current value
  // from inside a callback that never re-creates, or a second press during
  // the fade would queue a second step change.
  const fadingRef = useRef(false);
  const fadeTimer = useRef<number>();
  const fadeMs = useMemo(() => (prefersReducedMotion() ? 0 : FADE_MS), []);

  useEffect(() => () => window.clearTimeout(fadeTimer.current), []);

  // Where the reveal left the score, for the step after it to carry the
  // number on from. Only that one hand-over carries anything: every other
  // move clears it, so stepping back to the summary later finds it at rest.
  const [carriedScore, setCarriedScore] = useState<ScorePartRects>();
  const stageRef = useRef<HTMLDivElement>(null);

  // Read from refs rather than closed over: `goTo` and `back` must stay
  // stable for the same reason `forward` does, and the step list is rebuilt
  // every render.
  const stepsRef = useRef<ReportStep[]>([]);
  const stepRef = useRef(step);
  stepRef.current = step;

  const goTo = useCallback(
    (next: (current: number) => number) => {
      if (fadingRef.current) return;

      setCarriedScore(undefined);
      fadingRef.current = true;
      setFading(true);
      const target = stepsRef.current[next(stepRef.current)];
      setDeparting({ score: !target?.readout, controls: !target?.nav });
      fadeTimer.current = window.setTimeout(() => {
        setStep(next);
        setFading(false);
        setDeparting(undefined);
        fadingRef.current = false;
      }, fadeMs);
    },
    [fadeMs]
  );

  // Stable, so the reveal's own timer is not restarted by every re-render.
  const forward = useCallback(() => goTo((current) => current + 1), [goTo]);

  // The reveal's way out. No fade: the next step takes over with the score
  // exactly where the reveal drew it and moves it from there, so fading the
  // stage out would only blink the number it is about to carry.
  const carryScoreForward = useCallback(() => {
    if (fadingRef.current || !stageRef.current) {
      forward();
      return;
    }
    setCarriedScore(measureScoreParts(stageRef.current));
    setStep((current) => current + 1);
  }, [forward]);

  const back = useCallback(
    () =>
      goTo((current) => {
        let previous = current - 1;
        while (previous > 0 && stepsRef.current[previous]?.transient) {
          previous -= 1;
        }
        return Math.max(previous, 0);
      }),
    [goTo]
  );

  // Stores the verdict and emits the event Platform-side. The Mixpanel event
  // above stays: it is the live dashboard, and this is the record.
  const [persistRating] = useMutation(RATE_ASSESSMENT_REPORT);

  // The Index tab's selection plus the booked check-in, so the report reads
  // the learner's own scores in the same shape that tab caches them.
  // `all` rather than Apollo's default: a failure in one resolver otherwise
  // discards the whole response, and the report then renders its cover and
  // its closing screen as though that were the learner's result. Keeping the
  // partial data costs the report a card; discarding it costs the report.
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery(
    LEARNER_ASSESSMENT_REPORT_SCORES,
    {
      variables: { appInfo, historyLimit: 1 },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    }
  );

  const checkInDate = formatCheckInDate(
    data?.learner?.assessmentSchedule?.nextCheckInBookedAt,
    i18n.language
  );

  const scores = data?.learner?.indexScores;
  const liveReportCopy: ReportCopyNode | undefined =
    scores?.report ?? undefined;

  /**
   * The copy the current step renders, held for as long as the learner stays
   * on it. Generation takes long enough that it ordinarily finishes while the
   * learner is already reading, and a sentence rewriting itself mid-read is
   * the thing this prevents. Only the copy is held: the scores and everything
   * derived from them stay live.
   */
  const reportCopy = useStepSnapshot(step, liveReportCopy);

  // The ceiling on asking, measured from submit: the wait the learner is doing
  // began there, so a reload resumes it rather than starting it again, and a
  // response long past the ceiling skips the placeholder altogether. Platform
  // answers PENDING for any response without a report row, however old. The
  // earlier of submit and opening is used so a client clock running behind
  // the server's cannot stretch the wait past one ceiling from opening, and
  // opening alone stands in where there is no submit time. A client clock
  // running ahead of the server's shortens the wait by the skew.
  const [openedAt] = useState(() => Date.now());
  const submittedAt = scores?.takenAt ? Date.parse(scores.takenAt) : NaN;
  const waitEndsAt =
    (Number.isNaN(submittedAt) ? openedAt : Math.min(submittedAt, openedAt)) +
    REPORT_COPY_WAIT_MS;
  const [lapsedWaitEndsAt, setLapsedWaitEndsAt] = useState<number>();
  useEffect(() => {
    const timer = window.setTimeout(
      () => setLapsedWaitEndsAt(waitEndsAt),
      Math.max(0, waitEndsAt - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [waitEndsAt]);
  // Settled on the first render for a wait that ended before the screen
  // opened, so an old response shows no frame of placeholder.
  const askedLongEnough =
    waitEndsAt <= openedAt || lapsedWaitEndsAt === waitEndsAt;

  // PENDING is the one state worth asking again about: READY has the copy,
  // and neither UNAVAILABLE nor an absent report ever will. Read from the live
  // response rather than the step's snapshot, or the screen would keep asking
  // for copy it already has until the learner moved on.
  const stillGenerating = liveReportCopy?.status === "PENDING";
  useEffect(() => {
    if (!stillGenerating || askedLongEnough) {
      stopPolling();
      return undefined;
    }
    startPolling(REPORT_POLL_INTERVAL_MS);
    return stopPolling;
  }, [stillGenerating, askedLongEnough, startPolling, stopPolling]);

  /**
   * The summary step's copy, held the same way with one difference: a step
   * opened while the copy is being written holds nothing rather than the
   * PENDING node, so the copy fills the step in when it lands. The summary is
   * the first copy step and is ordinarily reached before generation finishes,
   * so holding PENDING there left a first-time learner reading stand-in copy
   * with no ancient wisdom card for the whole step. Its cards show a
   * placeholder while they wait, so what lands fills a gap rather than
   * rewriting prose. Once the asking stops, PENDING is held like any other
   * answer and the fallback copy stands in.
   *
   * A PENDING node that already carries the summary is held like a READY
   * one: the summary shows from it, and the READY copy landing later fills in
   * the steps after it without rewriting the summary under the learner.
   */
  const writingCopy = stillGenerating && !askedLongEnough;
  const summaryCopy = useStepSnapshot(
    step,
    writingCopy && !liveReportCopy?.partialAvailable
      ? undefined
      : liveReportCopy
  );

  // One report per failure rather than one per render, and one again if the
  // learner retries into a different failure. The messages go in the summary
  // because a resolver's own words are what says which part of the report
  // came back empty.
  const reportedFailure = useRef<string>();
  useEffect(() => {
    if (!error) {
      reportedFailure.current = undefined;
      return;
    }

    const detail = error.graphQLErrors.length
      ? error.graphQLErrors
          .map((graphQlError) => graphQlError.message)
          .join("; ")
      : error.message;
    if (reportedFailure.current === detail) return;

    reportedFailure.current = detail;
    logError(
      `Could not load the learner's resilience report: ${detail}`,
      error
    );
  }, [error]);

  // Only READY copy is the learner's own. PENDING has nothing to read yet, so
  // it stands in from the catalogue like every other unwritten report. The
  // summary is the exception: a PENDING node with the summary already written
  // carries the learner's own headline, modern measurement and ancient wisdom.
  const generated = reportCopy?.status === "READY" ? reportCopy : undefined;
  const summaryGenerated =
    summaryCopy?.status === "READY" || summaryCopy?.partialAvailable
      ? summaryCopy
      : undefined;

  /**
   * The first of these that has anything in it.
   *
   * Blank counts as absent at every tier, so a card is never drawn around an
   * empty paragraph and a blurb that came back empty falls through rather
   * than rendering a heading over nothing.
   *
   * Where a part had copy before any of it was generated, that copy is the
   * middle candidate rather than being dropped, so a learner whose report
   * never generated still reads something chosen for their result instead of
   * the one sentence everybody gets. How much that is worth differs by part,
   * which matters to anyone thinking of collapsing these tiers: the overall
   * blurb is band-granular, one of six, so everyone scoring Strong reads the
   * same sentence. The domain blurbs carry far more, being picked per domain
   * and per tier, and on top of that which domain is shown at all is this
   * learner's own strongest and weakest. The catalogue's stand-in is the last
   * resort, for a score with no metadata behind it.
   */
  const written = (...candidates: (string | null | undefined)[]): string =>
    candidates.find((candidate) => candidate?.trim()) ?? "";
  const scale = data?.scoreScale;
  const benchmark: PopulationBenchmarkNode | undefined =
    scores?.populationBenchmark ?? undefined;
  const overall: ScoreNode | undefined = scores?.overallIndex;
  const outOf = overall?.maxValue ?? 100;

  const domains: ScoreNode[] = useMemo(
    () => (scores?.domains ?? []).filter(isScored),
    [scores?.domains]
  );
  const themes: ScoreNode[] = useMemo(
    () => (scores?.themes ?? []).filter(isScored),
    [scores?.themes]
  );

  const ranked = useMemo(() => [...domains].sort(byValueDescending), [domains]);
  const breakdown = useMemo(
    () => [...domains].sort(byBreakdownOrder),
    [domains]
  );
  const strongest = ranked.slice(0, HIGHLIGHT_COUNT);
  const weakest = useMemo(
    () => ranked.slice(-HIGHLIGHT_COUNT).reverse(),
    [ranked]
  );

  // The lowest domains, each offering a goal, lowest first. Memoised beside
  // its siblings and above the early returns: a hook after them runs on some
  // renders and not others, which React counts as a changed hook order.
  const goals = useMemo(
    () => ranked.slice(-GOAL_CHOICE_COUNT).reverse(),
    [ranked]
  );

  // What makes the difference between a short report and no report at all.
  const hasScoreData =
    Boolean(overall && isScored(overall)) || domains.length > 0;

  const reportSource = useReportSource();
  const { chartInteracted } = useReportJourney({
    // Only once the steps are rendered, which waits on the scores.
    active: Boolean(data) && hasScoreData,
    stepIndex: step,
    steps: stepsRef,
    responseId: scores?.responseId ?? null,
    source: reportSource,
  });

  // Analytics names a domain by its shorthand, which the charts do not key
  // on: `id` is a database key.
  const domainTapped = (id: string) => {
    const shorthand = domains.find((domain) => domain.id === id)?.shorthand;
    if (shorthand) chartInteracted("domain_tap", shorthand);
  };
  const bandTapped = (key: string) => chartInteracted("band_tap", key);

  const nav = {
    back: t("assessment:report.nav.back"),
    forward: t("assessment:report.nav.forward"),
  };
  // Straight from the response, translated there. Only the colour is added.
  const bands = (scale?.bands ?? []).map((band: ScoreBandNode) => ({
    key: band.key,
    from: band.minValue,
    to: band.maxValue,
    tone: BAND_TONES[band.key],
    width: BAND_WIDTHS[band.key],
    label: band.name,
  }));

  // Which band a score is in is the server's verdict, carried by `labelKey`.
  // The pill words itself from the band that key names, and the axis
  // highlights that same band, so the two cannot disagree on a score sitting
  // exactly on a threshold. The score's own label is the fallback for a band
  // missing from the list, which is stale wording at worst rather than an
  // empty pill.
  const currentBandKey = overall?.labelKey ?? undefined;
  const bandLabel =
    bands.find((band) => band.key === currentBandKey)?.label ??
    overall?.label ??
    undefined;

  // What each band opens to say about itself, from the pill and from its
  // name in the chart header. Keyed on the server's band key, like the
  // tones; a band the catalogue has no sentence for keeps a plain label
  // rather than opening onto the raw key. The current band is looked up
  // even when the scale lacks it, since the pill still names it.
  const bandMeanings = Object.fromEntries(
    Array.from(
      new Set([
        ...bands.map((band) => band.key),
        ...(currentBandKey ? [currentBandKey] : []),
      ])
    ).flatMap((key) => {
      const meaningKey = `assessment:report.range.bandMeaning.${key}`;
      return i18n.exists(meaningKey) ? [[key, t(meaningKey)]] : [];
    })
  );

  /**
   * The score on the bands' own scale.
   *
   * The server states its bands on a normalised range and a score carries
   * its own bounds, which need not match. Normalising the same way the
   * server bands a value is what keeps the marker in the band the pill
   * names; skipping it is the failure the scale's own minValue/maxValue are
   * returned to prevent.
   */
  const valueOnScale = (() => {
    if (!overall || !scale || typeof overall.value !== "number")
      return undefined;
    const from = overall.minValue ?? 0;
    const to = overall.maxValue ?? 100;
    if (to === from) return scale.minValue;
    const fraction = (overall.value - from) / (to - from);
    return scale.minValue + fraction * (scale.maxValue - scale.minValue);
  })();

  const scoreReadout: ReportStepScore | undefined = overall
    ? {
        value: overall.value as number,
        outOf,
        label: t("assessment:report.score.label"),
      }
    : undefined;

  // Themes are the level beneath a domain, so the ones that roll up to a
  // domain are what contributes to it. Matched on `domainShorthand`, which
  // the schema requires: matching on anything else is how this screen came
  // to head a list of Mind themes "What contributes towards Nutrition".
  const contributorsFor = (domain?: ScoreNode) => {
    if (!domain?.shorthand) return [];
    return themes
      .filter(
        (theme) => theme.name && theme.domainShorthand === domain.shorthand
      )
      .slice(0, HIGHLIGHT_COUNT)
      .map((theme) => ({
        value: theme.value as number,
        outOf: theme.maxValue ?? 100,
        label: theme.name as string,
      }));
  };

  // Each tile carries what the card below shows when it is picked, so
  // selecting a tile is a local state change rather than a re-query.
  const tilesFrom = (group: ScoreNode[], headingKey: string) =>
    group.map((domain) => ({
      id: domain.id,
      value: domain.value as number,
      outOf: domain.maxValue ?? 100,
      label: domain.name ?? "",
      icon: iconFor(domain),
      contributorsHeading: (
        <Trans
          i18nKey={headingKey}
          values={{ domain: domain.name ?? "" }}
          components={{ icon: iconFor(domain) ?? <span /> }}
        />
      ),
      contributors: contributorsFor(domain),
    }));

  const leave = () => navigate(INDEX_TAB_PATH, { replace: true });

  const retry = () =>
    refetch().catch((refetchError) =>
      logError("Could not reload the learner's resilience report", refetchError)
    );

  // A report with no copy is ordinary and renders from the catalogue. A
  // report with no scores because the request failed is not, and handing the
  // learner the cover and the closing screen presents a failure as their
  // result. Only the second gets the error treatment, which is the Index
  // tab's own: the two screens read the same query.
  if (error && !hasScoreData) {
    return (
      <ReportShell showSky onClose={leave}>
        <div className={styles.failure} data-testid="report-error">
          <PageError className={styles["failure-message"]} />
          <Button variant="primary" onClick={retry} loading={loading}>
            {t("common:action.tryAgain")}
          </Button>
        </div>
      </ReportShell>
    );
  }

  // A learner who has never submitted an assessment has no scores and no
  // failure either: the query settled and there is simply nothing to read.
  // The Index tab already asks them to take their assessment, so that is
  // where they belong rather than on a cover promising a report they never
  // took. `data` is what says a response actually landed, since `loading` is
  // false on a render where the query has not run at all. A report node is
  // what separates them from someone who submitted seconds ago: that learner
  // has a response being scored, and the screen waits on the poll for it.
  if (!loading && !error && data && !hasScoreData && !liveReportCopy) {
    return <Navigate to={INDEX_TAB_PATH} replace />;
  }

  // Back to the cover, so a replay reads the same way it did the first time.
  const replay = () => goTo(() => 0);

  const goalChoices = goals.map((domain) => ({
    id: domain.id,
    label: domain.name ?? "",
    accessibleLabel: t("assessment:report.ori.choice", {
      domain: domain.name ?? "",
    }),
    icon: iconFor(domain),
    value: domain.value as number,
    outOf: domain.maxValue ?? 100,
    accessibleValue: t("assessment:report.ori.choiceScore", {
      value: domain.value,
      max: domain.maxValue ?? 100,
    }),
  }));

  /**
   * Hands the goal to Ori the way every other entry point does: stash the
   * prompt, then navigate, and let the chat send it once its socket is up.
   * `wasStarterSentRecently` is why a second click drops the learner into the
   * conversation they already have rather than repeating the prompt, which
   * makes Ori answer as though it were being nagged.
   */
  /**
   * Records whether the report helped.
   *
   * An event rather than a row: the question this answers is what proportion
   * of learners found the report useful, and that is a ratio over a
   * dimension, not something anything in the product reads back. It carries
   * the response it is about, so a rating can be joined to the scores that
   * produced it if the answer ever needs to be cut by them.
   *
   * A learner can change their mind, and each press is its own event; the
   * last one for a response is the one that counts.
   */
  /**
   * Hands the learner's score to whoever they choose, as a sentence.
   *
   * No link and no page a stranger could open. A resilience score is
   * health-adjacent, so the learner picking the recipient is the whole of the
   * privacy model, and nothing is published that would later need revoking.
   * It never names their employer, which would turn a personal score into a
   * statement about a company.
   *
   * With the share image on, the learner previews an image instead and this
   * sentence is the fallback, on the learner's next press, when the image
   * cannot be made. The image holds the same score and adds the radar's
   * shape under the domain names, with no value printed; still no name,
   * company, Ori copy or link. It leaves only by the learner's own press,
   * through the share sheet or a PNG download.
   *
   * Where there is no share sheet the same sentence goes to the clipboard,
   * so the button is real on a desktop browser rather than drawn and dead.
   * Nothing has left the app at that point: the learner still has to paste.
   */
  const shareScore = async () => {
    const text = t("assessment:report.ori.shareText", {
      value: Math.round(overall?.value as number),
      max: outOf,
    });
    const method = navigator.share ? "Share sheet" : "Clipboard";

    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        setShareCopied(true);
        window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(
          () => setShareCopied(false),
          COPIED_CONFIRMATION_MS
        );
      }
    } catch (error) {
      // Dismissing the sheet rejects with AbortError. Deciding not to share
      // is the learner using the control correctly, not a fault, and
      // reporting it would bury the real failures underneath.
      if ((error as Error)?.name === "AbortError") return;
      logError("Could not share the report score", error);
      return;
    }

    // Where it went is between the learner and their share sheet. The method
    // is all this can honestly claim to know.
    trackEvent("Report Score Shared", {
      Method: method,
      // What was shared, alongside how it left.
      "Share Method": "Text",
      "Response Id": scores?.responseId ?? null,
      Score: overall?.value ?? null,
      "Current Page": window.location.href,
    });
  };

  // A download leaves by neither the share sheet nor the clipboard, so it
  // has no Method.
  const trackImageShared = (shareMethod: ImageShareMethod) =>
    trackEvent("Report Score Shared", {
      Method: shareMethod === "Image share" ? "Share sheet" : null,
      "Share Method": shareMethod,
      "Response Id": scores?.responseId ?? null,
      Score: overall?.value ?? null,
      "Current Page": window.location.href,
    });

  const rateReport = (helpful: boolean) => {
    setFeedbackHelpful(helpful);

    trackEvent("Report Rated", {
      Helpful: helpful,
      "Response Id": scores?.responseId ?? null,
      Score: overall?.value ?? null,
      "Current Page": window.location.href,
    });

    // A rating with nothing to attach it to cannot be read back per learner,
    // and is a bare ratio the event stream already gives. The thumb still
    // shows as pressed either way: the learner answered the question, and
    // whether we could file the answer is our problem rather than theirs.
    if (!scores?.responseId) return;

    persistRating({
      variables: {
        appInfo,
        responseId: scores.responseId,
        helpful,
        // The copy that was on screen when they pressed, which is the step's
        // snapshot rather than anything that has landed since. A thumb on the
        // catalogue's stand-in and a thumb on the learner's own words are
        // different findings, and only this tells them apart. Sent as nothing
        // where there is no report node, because null there would be a guess.
        ...(reportCopy ? { reportCopyStatus: reportCopy.status } : {}),
      },
    }).catch((error) =>
      logError("Could not record the learner's report rating", error)
    );
  };

  const chooseGoal = (choiceId: string) => {
    const domain = goals.find((candidate) => candidate.id === choiceId);
    const prompt = t("assessment:report.ori.prompt", {
      domain: domain?.name ?? "",
    });
    const reprompted = !wasStarterSentRecently(prompt);

    trackEvent("Report Goal Chosen", {
      Domain: domain?.shorthand ?? null,
      "Domain Score": domain?.value ?? null,
      Destination: ORI_PATH,
      Reprompted: reprompted,
      "Current Page": window.location.href,
    });

    if (reprompted) setOriStarterMessage(prompt, ORI_ENTRY_POINT);
    navigate(ORI_PATH);
  };

  /**
   * The population card, assembled from what the server measured.
   *
   * Absent when no benchmark has been built, and the step drops the card
   * rather than rendering a gap. Nothing here is derived: the percentile,
   * the average and the spread are all read straight off the response,
   * because the sentence this produces is a claim about other people and a
   * plausible invention would be indistinguishable from a measurement.
   */
  const percentile = benchmark
    ? {
        value: overall?.value as number,
        // Rounded for display only. The server returns the measured mean to
        // full precision, and "Average user 58.97425496570194" is a true
        // number that reads as a bug.
        averageValue: Math.round(benchmark.averageValue),
        distribution: benchmark.distribution,
        // The design says "top N%", which is the share above the learner.
        heading: t("assessment:report.breakdown.percentile.heading"),
        badge: t("assessment:report.breakdown.percentile.badge", {
          percent: Math.max(1, Math.round(100 - benchmark.percentile)),
        }),
        youLabel: t("assessment:report.breakdown.percentile.you"),
        averageLabel: t("assessment:report.breakdown.percentile.average"),
        // Read off the window the benchmark carries, so the sentence stays
        // true if it is ever rebuilt over a different span. A twelve-month
        // window gets the design's own wording rather than "12 months".
        footnote: (() => {
          const months = monthsBetween(
            benchmark.windowStart,
            benchmark.windowEnd
          );
          if (months === null) return undefined;
          return months === MONTHS_PER_YEAR
            ? t("assessment:report.breakdown.percentile.footnoteYear")
            : t("assessment:report.breakdown.percentile.footnote", {
                count: months,
              });
        })(),
      }
    : undefined;

  /**
   * The one part that is left out rather than approximated when it was never
   * generated. The step drops the whole card on an empty body, so nothing is
   * drawn where the sentence would have been.
   */
  const ancientWisdom = summaryGenerated?.ancientWisdom?.trim()
    ? summaryGenerated.ancientWisdom
    : SHOW_ANCIENT_WISDOM_FALLBACK
    ? t("assessment:report.generated.ancientWisdom")
    : undefined;

  const controls: ReportStepNav = {
    onBack: back,
    backLabel: nav.back,
    onForward: forward,
    forwardLabel: nav.forward,
  };

  // Built from what the learner actually has. A step with nothing to say is
  // left out rather than rendered empty, so someone missing domain scores
  // still gets a report that reads correctly — it is simply shorter.
  const steps: ReportStep[] = [
    {
      key: "cover",
      withoutClose: true,
      brightSky: true,
      node: <ReportReadyStep firstName={user?.firstName} onStart={forward} />,
    },
  ];

  if (overall && isScored(overall)) {
    steps.push({
      key: "reveal",
      transient: true,
      withoutClose: true,
      brightSky: true,
      node: (
        <ScoreRevealStep
          key="reveal"
          score={overall.value as number}
          outOf={outOf}
          eyebrow={t("assessment:report.reveal.eyebrow")}
          onDone={carryScoreForward}
        />
      ),
    });
  }

  if (overall && isScored(overall)) {
    steps.push({
      key: "summary",
      node: (
        <SummaryNarrativeStep
          title={written(
            summaryGenerated?.reportHeadline,
            t("assessment:report.generated.headline")
          )}
          attribution={t("assessment:report.summary.attribution")}
          attributionIconSrc={OriMark}
          // Generated, then the band's own blurb, then copy for everybody.
          narrative={written(
            summaryGenerated?.modernMeasurement,
            overall.blurb,
            t("assessment:report.generated.modernMeasurement")
          )}
          secondaryTitle={t("assessment:report.summary.ancientWisdomTitle")}
          secondaryBody={ancientWisdom}
          pending={!summaryCopy && writingCopy}
          pendingLabel={t("assessment:report.summary.writing")}
          score={scoreReadout as never}
          scoreEyebrow={t("assessment:report.reveal.eyebrow")}
          carriedFrom={carriedScore}
        />
      ),
      nav: controls,
    });
  }

  if (overall && isScored(overall) && bands.length > 0) {
    steps.push({
      key: "range",
      node: (
        <ScoreRangeStep
          heading={t("assessment:report.range.heading", {
            value: overall.value as number,
          })}
          bandLabel={bandLabel}
          currentBandKey={currentBandKey}
          bandMeanings={bandMeanings}
          bands={bands}
          value={overall.value as number}
          valueOnScale={valueOnScale}
          scaleLabel={t("assessment:report.range.scaleLabel")}
          scoreCaption={t("assessment:report.range.scoreCaption")}
          onBandOpen={bandTapped}
        />
      ),
      nav: controls,
      readout: scoreReadout,
    });
  }

  if (domains.length > 0) {
    steps.push({
      key: "breakdown",
      node: (
        <ScoreBreakdownStep
          heading={t("assessment:report.breakdown.heading")}
          domains={breakdown.map((domain) => ({
            id: domain.id,
            label: domain.name ?? "",
            score: domain.value as number,
            icon: iconFor(domain),
          }))}
          outOf={outOf}
          radarLabel={t("assessment:report.breakdown.radarLabel")}
          barsLabel={t("assessment:report.breakdown.barsLabel")}
          missingScoreLabel={t("assessment:report.breakdown.missingScore")}
          percentile={percentile}
          emptyMessage={t("assessment:report.breakdown.empty")}
          onDomainOpen={domainTapped}
        />
      ),
      nav: controls,
      readout: scoreReadout,
    });
  }

  if (strongest.length > 0) {
    steps.push({
      key: "strongest",
      node: (
        // Keyed, because the growth step is the same component in the same
        // position: without distinct keys React keeps one instance across
        // both and the second arrives on whichever tile the first left
        // selected.
        <DomainHighlightsStep
          key="strengths"
          heading={t("assessment:report.strengths.heading")}
          narrative={written(
            generated?.strengthsFocus,
            strongest[0].blurb,
            t("assessment:report.generated.strengthsFocus")
          )}
          tiles={tilesFrom(
            strongest,
            "assessment:report.strengths.contributorsHeading"
          )}
          // The copy is written about the anchor, which is not always the
          // highest-scoring domain, so arriving on the first tile is what
          // put a Nutrition tile beside a paragraph about Sleep.
          initialSelectedId={tileIdFor(generated?.anchorDomain, strongest)}
          tilesLabel={t("assessment:report.strengths.tilesLabel")}
          noContributorsMessage={t("assessment:report.noContributors")}
          onSelect={domainTapped}
        />
      ),
      nav: controls,
      readout: scoreReadout,
    });
  }

  if (weakest.length > 0) {
    steps.push({
      key: "opportunity",
      node: (
        <DomainHighlightsStep
          key="growth"
          heading={t("assessment:report.growth.heading")}
          narrative={written(
            generated?.areasForGrowth,
            weakest[0].blurb,
            t("assessment:report.generated.areasForGrowth")
          )}
          tiles={tilesFrom(
            weakest,
            "assessment:report.growth.contributorsHeading"
          )}
          initialSelectedId={tileIdFor(generated?.leadGrowthDomain, weakest)}
          tilesLabel={t("assessment:report.growth.tilesLabel")}
          noContributorsMessage={t("assessment:report.noContributors")}
          onSelect={domainTapped}
        />
      ),
      nav: controls,
      readout: scoreReadout,
    });
  }

  // The image the learner previews and shares: the radar, which names each
  // domain and prints none of their values, and no overall score. The card
  // takes no name, company, Ori copy or link, so none can reach it.
  const shareCard: ShareCardProps | undefined =
    overall && isScored(overall)
      ? {
          outOf,
          domains: breakdown.map((domain) => ({
            id: domain.shorthand ?? domain.id,
            label: domain.name ?? "",
            score: domain.value as number,
          })),
          order: BREAKDOWN_ORDER,
          heading: t("assessment:report.ori.shareCard.heading"),
          title: t("assessment:report.ori.shareCard.title", {
            value: Math.round(overall.value as number),
            max: outOf,
          }),
          logoSrc: WoneLogo,
          skySrc: SkyPosterTall,
        }
      : undefined;

  // Nothing to share without a score, so the step draws the button disabled.
  // The image can always be downloaded.
  const onShare = shareCard ? () => setPreviewingShare(true) : undefined;

  steps.push({
    key: "chat_with_ori",
    withoutClose: true,
    node: (
      <ChatWithOriStep
        heading={t("assessment:report.ori.heading", {
          name: user?.firstName ?? "",
        })}
        choicesHeading={t("assessment:report.ori.choicesHeading")}
        choices={goalChoices}
        onReplay={replay}
        replayLabel={t("assessment:report.ori.replay")}
        onDone={leave}
        doneLabel={t("assessment:report.ori.done")}
        shareLabel={
          shareCopied
            ? t("assessment:report.ori.shareCopied")
            : t("assessment:report.ori.share")
        }
        onShare={onShare}
        onFeedback={rateReport}
        feedbackHelpful={feedbackHelpful}
        feedbackPrompt={t("assessment:report.ori.feedbackPrompt")}
        feedbackHelpfulLabel={t("assessment:report.ori.feedbackHelpful")}
        feedbackUnhelpfulLabel={t("assessment:report.ori.feedbackUnhelpful")}
        onChoose={chooseGoal}
      />
    ),
  });

  // A reopened report starts on the first content step: the first with the
  // step controls, which the cover, the reveal and the closing step lack.
  // Found by shape rather than key, so renaming a step cannot send a reopen
  // back to the cover. Set during render so the cover never paints first.
  // Until the scores land there is no content step to start on, so the frame
  // waits on its sky; a report with no content step opens as it always has.
  if (openingOnContent) {
    const contentStep = steps.findIndex(
      (entry, index) => index > 0 && !entry.transient && Boolean(entry.nav)
    );
    if (contentStep > 0) {
      setOpeningOnContent(false);
      setStep(contentStep);
    } else if (loading) {
      return <ReportShell showSky>{null}</ReportShell>;
    } else {
      setOpeningOnContent(false);
    }
  }

  stepsRef.current = steps;

  if (step >= steps.length) return <Navigate to={INDEX_TAB_PATH} replace />;

  const onOri = steps[step].key === "chat_with_ori";
  if (
    onOri &&
    !reopened &&
    checkInDate &&
    (toast.phase === "unseen" || toast.phase === "waiting")
  ) {
    setToast({ phase: "showing", date: checkInDate });
  } else if (onOri && !reopened && toast.phase === "unseen") {
    setToast({ phase: "waiting", date: null });
  } else if (
    !onOri &&
    (toast.phase === "showing" || toast.phase === "waiting")
  ) {
    setToast((current) => ({ ...current, phase: "spent" }));
  }

  // The designs pip the screens the learner moves through: not the cover,
  // and not the reveal, which plays on a timer and carries itself forward.
  // Counting the reveal put every screen one pip further along than the
  // frames show it. The total is derived rather than a constant, because a
  // learner missing domain scores is shown fewer screens.
  const pipTotal = steps.filter(
    (entry, index) => index > 0 && !entry.transient
  ).length;
  const currentPip = steps
    .slice(1, step + 1)
    .filter((entry) => !entry.transient).length;
  const progress =
    currentPip > 0 && pipTotal > 0
      ? {
          current: currentPip,
          total: pipTotal,
          label: t("assessment:report.progress", {
            current: currentPip,
            total: pipTotal,
          }),
        }
      : undefined;

  const { nav: stepNav, readout, withoutClose, brightSky } = steps[step];

  return (
    <ReportShell
      showSky
      brightSky={brightSky}
      onClose={withoutClose ? undefined : leave}
      progress={progress}
      score={readout}
      {...stepNav}
      // The reveal hands its score to the next step without a fade, so the
      // controls that step brings have nothing to fade in under and arrive
      // on their own, as the step's other furniture does.
      controlsArriving={Boolean(carriedScore) && !prefersReducedMotion()}
      departing={departing}
      overlay={
        <CheckInToast
          message={
            toast.phase === "showing" && toast.date
              ? t("assessment:report.chatWithOri.checkInAdded", {
                  date: toast.date,
                })
              : undefined
          }
          leaving={fading}
          onDone={() => setToast((current) => ({ ...current, phase: "spent" }))}
        />
      }
    >
      <div
        ref={stageRef}
        className={clsx(styles.stage, fading && styles.fading)}
        style={{ "--report-fade": `${fadeMs}ms` } as React.CSSProperties}
      >
        {steps[step].node}
      </div>
      {previewingShare && shareCard && (
        <SharePreview
          card={shareCard}
          onClose={() => setPreviewingShare(false)}
          onShared={(method) => {
            trackImageShared(method);
            setPreviewingShare(false);
          }}
          // No image to share, so the score goes as the text it always did.
          onShareText={
            canShareText()
              ? () => {
                  setPreviewingShare(false);
                  void shareScore();
                }
              : undefined
          }
        />
      )}
    </ReportShell>
  );
};

export default AssessmentReport;
